// Shared helpers for deriving category data from the products collection.
// A product's image is the single source of truth for category card imagery,
// so the storefront never shows a stock photo when a real product exists.

import { getProductVariants } from './productVariants';

// Pull the best available image URL off a product document.
export function productImage(p) {
  if (!p) return '';
  if (Array.isArray(p.images) && p.images[0]) return p.images[0];
  return p.imageUrl || p.image_url || p.image || '';
}

// A product is visible on the storefront only when an admin has published it
// (webActive === true). See utils/productVisibility.js — re-exported here so
// the existing callers keep working.
export { isWebProduct as isVisibleProduct } from './productVisibility';

const normName = (s) => (s || '').trim().toLowerCase();

// First photo of a product that isn't flagged out of stock (those render
// blurred on the storefront, so they make a poor cover image).
function coverImage(p) {
  const v = getProductVariants(p).find((x) => !x.unavailable && x.image);
  return v ? v.image : '';
}

// One category card per category that has at least one published product,
// each showing a photo of a randomly picked product from that category.
// `categoryDocs` (the `categories` collection) only supplies metadata —
// display name, description, order — and never decides visibility, so a
// category with stock shows up even without a doc, and one without stock
// never does. Pass products already filtered with isVisibleProduct.
export function buildLiveCategories(products = [], categoryDocs = []) {
  const meta = {};
  for (const c of categoryDocs) {
    if (c && c.name && c.active !== false) meta[normName(c.name)] = c;
  }

  const groups = {};
  for (const p of products) {
    const key = normName(p.category);
    if (!key) continue;
    if (!groups[key]) groups[key] = { name: p.category.trim(), products: [] };
    groups[key].products.push(p);
  }

  const pick = (list) => list[Math.floor(Math.random() * list.length)];

  return Object.entries(groups)
    .map(([key, g]) => {
      const m = meta[key] || {};
      const covers = g.products.map(coverImage).filter(Boolean);
      const anyImages = g.products.map(productImage).filter(Boolean);
      return {
        id: m.id || key,
        name: m.name || g.name,
        description: m.description || '',
        order: typeof m.order === 'number' ? m.order : null,
        count: g.products.length,
        image: (covers.length ? pick(covers) : pick(anyImages)) || m.image || '',
      };
    })
    .sort((a, b) => {
      // Admin-set order first, then the categories with the most stock.
      if (a.order !== null && b.order !== null && a.order !== b.order) return a.order - b.order;
      if (a.order !== null && b.order === null) return -1;
      if (a.order === null && b.order !== null) return 1;
      return b.count - a.count;
    });
}
